import React from "react";
import { ArrowRight, Phone, ShieldCheck, Award, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden grain"
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/12901948/pexels-photo-12901948.jpeg"
          alt="Plaster texture"
          className="h-full w-full object-cover scale-110 animate-[fade-up_1.4s_ease-out_both]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/85 via-brand-ink/70 to-brand-ink" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.18),transparent_55%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-24 md:pb-32 w-full">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-brand-gold/90 animate-fade-up">
          <span className="inline-block h-px w-10 bg-brand-gold" />
          Established Hampshire trade · Domestic & Commercial
        </div>

        <h1
          data-testid="hero-headline"
          className="mt-6 font-serif text-white text-balance text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight max-w-5xl animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          Walls that <em className="text-brand-gold not-italic">last</em>.
          <br />
          Finishes that <span className="italic">speak</span> for themselves.
        </h1>

        <p
          className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          Renderwall Ltd delivers premium rendering, plastering and drylining
          across {SITE.area}. Honest quotes, clean sites and a finish you&apos;ll
          be proud to show off.
        </p>

        <div
          className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up"
          style={{ animationDelay: "360ms" }}
        >
          <Button
            asChild
            data-testid="cta-quote-hero-button"
            className="bg-brand-gold hover:bg-brand-goldDark text-black font-semibold tracking-wide rounded-sm px-8 py-6 text-base group"
          >
            <a href="#contact" className="inline-flex items-center gap-2">
              Get my free quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid="cta-call-hero-button"
            className="bg-transparent border border-brand-gold/60 text-brand-gold hover:bg-brand-gold hover:text-black rounded-sm px-8 py-6 text-base"
          >
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="inline-flex items-center gap-2"
            >
              <Phone className="h-4 w-4" /> Call {SITE.phone}
            </a>
          </Button>
        </div>

        <div
          className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl animate-fade-up"
          style={{ animationDelay: "480ms" }}
        >
          {[
            { Icon: ShieldCheck, label: "Fully insured" },
            { Icon: Award, label: "20+ years on the tools" },
            { Icon: MapPin, label: "Hampshire & South East" },
          ].map(({ Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-3 text-white/80"
            >
              <Icon className="h-5 w-5 text-brand-gold" />
              <span className="text-sm font-medium tracking-wide">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
